import { useEffect, useMemo, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { uploadMediaFile } from "@/lib/media";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Pencil, RotateCcw, Sparkles, Trash2, Upload, Check, X, ArrowUpDown, Shrink } from "lucide-react";
import { toast } from "sonner";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const thumb = (m) => `${fileUrl(m.url)}${m.updated_at ? `?v=${encodeURIComponent(m.updated_at)}` : ""}`;

const SORTS = {
  added_desc: { label: "Date added — newest first", fn: (a, b) => (b.created_at || "").localeCompare(a.created_at || "") },
  added_asc: { label: "Date added — oldest first", fn: (a, b) => (a.created_at || "").localeCompare(b.created_at || "") },
  modified_desc: { label: "Date modified — newest first", fn: (a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || "") },
  modified_asc: { label: "Date modified — oldest first", fn: (a, b) => (a.updated_at || a.created_at || "").localeCompare(b.updated_at || b.created_at || "") },
  size_desc: { label: "Size — largest first", fn: (a, b) => (b.size || 0) - (a.size || 0) },
  size_asc: { label: "Size — smallest first", fn: (a, b) => (a.size || 0) - (b.size || 0) },
};

function NameEditor({ item, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const save = async () => {
    const n = name.trim();
    if (!n || n === item.name) { setEditing(false); setName(item.name); return; }
    try {
      await api.patch(`/admin/media/${item.id}`, { display_name: n });
      toast.success("Renamed");
      setEditing(false);
      onSaved();
    } catch {
      toast.error("Rename failed");
    }
  };
  if (!editing) {
    return (
      <button type="button" onClick={() => { setName(item.name); setEditing(true); }} className="group/name flex items-center gap-1.5 min-w-0 text-left" title="Rename" data-testid={`media-name-${item.id}`}>
        <span className="text-xs font-medium truncate">{item.name}</span>
        <Pencil className="w-3 h-3 text-zinc-300 group-hover/name:text-zinc-600 shrink-0" />
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} autoFocus className="h-7 text-xs" data-testid={`media-name-input-${item.id}`} />
      <button type="button" onClick={save} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" aria-label="Save name" data-testid={`media-name-save-${item.id}`}><Check className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => setEditing(false)} className="p-1 text-zinc-400 hover:bg-zinc-100 rounded" aria-label="Cancel"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState("");
  const [uploading, setUploading] = useState(0);
  const [sortBy, setSortBy] = useState("added_desc");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/media");
      setItems(data);
    } catch {
      toast.error("Could not load media");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => [...items].sort(SORTS[sortBy]?.fn || SORTS.added_desc.fn), [items, sortBy]);

  const selectedIds = Object.keys(sel).filter((k) => sel[k]);
  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const toggleAll = () => setSel(allSelected ? {} : Object.fromEntries(items.map((i) => [i.id, true])));

  const onUpload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setUploading(files.length);
    let ok = 0;
    for (const f of files) {
      try { await uploadMediaFile(f); ok += 1; } catch { /* counted below */ }
      setUploading((n) => n - 1);
    }
    if (ok) toast.success(`Uploaded ${ok} photo${ok === 1 ? "" : "s"}`);
    if (ok < files.length) toast.error(`${files.length - ok} upload${files.length - ok === 1 ? "" : "s"} failed`);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const run = async (action, ids, label) => {
    if (!ids.length) return;
    setBusy(action);
    try {
      const { data } = await api.post(`/admin/media/${action}`, { ids });
      if (action === "delete") {
        toast.success(`Deleted ${data.deleted} photo${data.deleted === 1 ? "" : "s"}`);
      } else if (action === "compress") {
        const done = (data.compressed || []).length;
        const skipped = (data.skipped || []).length;
        if (done) {
          const savedMb = (data.saved_bytes || 0) / 1048576;
          toast.success(`Compressed ${done} photo${done === 1 ? "" : "s"} — saved ${savedMb >= 1 ? `${savedMb.toFixed(1)} MB` : `${Math.round((data.saved_bytes || 0) / 1024)} KB`}${skipped ? ` · ${skipped} skipped` : ""}`);
        } else {
          toast.error(`No photos compressed — ${skipped} skipped (already under 500 KB or wouldn't shrink further)`);
        }
      } else {
        const done = (data.enhanced || data.reverted || []).length;
        const skipped = (data.skipped || []).length;
        if (done) toast.success(`${label} ${done} photo${done === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped` : ""}`);
        else toast.error(`No photos ${label.toLowerCase()} — ${skipped} skipped (already enhanced or unreadable)`);
      }
      setSel({});
      load();
    } catch {
      toast.error(`${label} failed`);
    } finally {
      setBusy("");
    }
  };
  const del = (ids) => {
    if (!ids.length) return;
    if (window.confirm(`Delete ${ids.length} photo${ids.length === 1 ? "" : "s"}? Pages using them will show a broken image until you replace it.`)) {
      run("delete", ids, "Deleted");
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="media-library">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Media Library</h1>
          <p className="text-sm text-zinc-500 mt-1">Every photo uploaded anywhere on the site. Enhance, rename, delete — in bulk or one by one.</p>
        </div>
        <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2.5 bg-zinc-900 text-white text-sm rounded-md hover:bg-black" data-testid="media-upload-btn">
          {uploading > 0 ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading > 0 ? `Uploading… ${uploading} left` : "Upload photos"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="media-upload-input" />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3 bg-white border border-zinc-200 rounded-md px-4 py-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="media-select-all" />
          {selectedIds.length ? `${selectedIds.length} selected` : "Select all"}
        </label>
        <div className="flex-1" />
        <div className="flex items-center gap-1.5">
          <ArrowUpDown className="w-3.5 h-3.5 text-zinc-400" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 w-[210px] text-xs" data-testid="media-sort-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SORTS).map(([k, s]) => (
                <SelectItem key={k} value={k} className="text-xs" data-testid={`media-sort-${k}`}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button type="button" disabled={!selectedIds.length || !!busy} onClick={() => run("compress", selectedIds, "Compressed")} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50 disabled:opacity-40" data-testid="media-compress-selected">
          {busy === "compress" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shrink className="w-3.5 h-3.5" />} Compress selected
        </button>
        <button type="button" disabled={!selectedIds.length || !!busy} onClick={() => run("enhance", selectedIds, "Enhanced")} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50 disabled:opacity-40" data-testid="media-enhance-selected">
          {busy === "enhance" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Enhance selected
        </button>
        <button type="button" disabled={!selectedIds.length || !!busy} onClick={() => del(selectedIds)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-red-200 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-40" data-testid="media-delete-selected">
          <Trash2 className="w-3.5 h-3.5" /> Delete selected
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-zinc-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-zinc-400">No photos yet — upload some to get started.</div>
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {sorted.map((m) => (
            <div key={m.id} className={`group bg-white border rounded-md overflow-hidden transition-shadow hover:shadow-md ${sel[m.id] ? "border-zinc-900 ring-1 ring-zinc-900" : "border-zinc-200"}`} data-testid={`media-card-${m.id}`}>
              <div className="relative aspect-[4/3] bg-zinc-100">
                <img src={thumb(m)} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute top-2 left-2">
                  <Checkbox checked={!!sel[m.id]} onCheckedChange={() => toggle(m.id)} className="bg-white/90" data-testid={`media-check-${m.id}`} />
                </div>
                <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                  {m.enhanced && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded" data-testid={`media-enhanced-badge-${m.id}`}>
                      <Sparkles className="w-2.5 h-2.5" /> Enhanced
                    </span>
                  )}
                  {m.compressed && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded" data-testid={`media-compressed-badge-${m.id}`}>
                      <Shrink className="w-2.5 h-2.5" /> Compressed
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 space-y-2">
                <NameEditor item={m} onSaved={load} />
                <div className="text-[11px] text-zinc-400" data-testid={`media-meta-${m.id}`}>
                  {fmtSize(m.size)}
                  {sortBy.startsWith("modified")
                    ? (m.updated_at || m.created_at ? ` · edited ${(m.updated_at || m.created_at).slice(0, 10)}` : "")
                    : (m.created_at ? ` · ${m.created_at.slice(0, 10)}` : "")}
                </div>
                <div className="flex items-center gap-1.5 pt-1">
                  {m.enhanced || m.compressed ? (
                    <button type="button" disabled={!!busy} onClick={() => run("revert", [m.id], "Reverted")} className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 border border-zinc-300 rounded hover:bg-zinc-50 disabled:opacity-40" data-testid={`media-revert-${m.id}`}>
                      <RotateCcw className="w-3 h-3" /> Revert
                    </button>
                  ) : (
                    <button type="button" disabled={!!busy} onClick={() => run("enhance", [m.id], "Enhanced")} className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 border border-zinc-300 rounded hover:bg-zinc-50 disabled:opacity-40" data-testid={`media-enhance-${m.id}`}>
                      <Sparkles className="w-3 h-3" /> Enhance
                    </button>
                  )}
                  {!m.compressed && m.size > 512000 && (
                    <button type="button" disabled={!!busy} onClick={() => run("compress", [m.id], "Compressed")} className="inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 disabled:opacity-40" title="Compress to a lighter file" data-testid={`media-compress-${m.id}`}>
                      <Shrink className="w-3 h-3" /> Compress
                    </button>
                  )}
                  <button type="button" disabled={!!busy} onClick={() => del([m.id])} className="inline-flex items-center justify-center text-[11px] px-2 py-1.5 border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-40" aria-label="Delete" data-testid={`media-delete-${m.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
